import React, { useState } from 'react';
import MessageListItem from './MessageListItem';

const MessageList = ({ 
  conversations = [], 
  selectedConversation, 
  onSelectConversation,
  searchQuery = '',
  onSearchChange,
  activeTab = 'applicants',
  onTabChange,
  filter = 'all',
  onFilterChange,
  organizationType = '',
  onOrganizationTypeChange,
  stats = { total: 0, unread: 0, unanswered: 0, directUnread: 0, supportUnread: 0 }
}) => {
  const directUnread = stats.directUnread || 0;
  const supportUnread = stats.supportUnread ?? stats.unread ?? 0;

  const filteredConversations = conversations.filter(conv => {
    // Filter by tab
    if (activeTab === 'direct') {
      if (!conv.isDirectMessage) return false;
    }
    if (activeTab === 'applicants') {
      if (conv.isInternal || conv.isDirectMessage) return false;
    }
    if (activeTab === 'staff' && !conv.isInternal) return false;
    if (activeTab === 'support') {
      if (!conv.isSupportTicket) return false;
    }
    
    // Filter by filter type
    if (filter === 'unread' && conv.unreadCount === 0) return false;
    if (filter === 'unanswered') {
      const lastMsg = conv.messages?.[conv.messages.length - 1];
      if (!lastMsg || !lastMsg.fromCustomerId) return false;
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        conv.licenceName?.toLowerCase().includes(query) ||
        conv.customerName?.toLowerCase().includes(query) ||
        conv.licenceRefNumber?.toLowerCase().includes(query) ||
        (conv.isDirectMessage && 'direct'.includes(query)) ||
        (conv.isSupportTicket && conv.customerEmail?.toLowerCase().includes(query))
      );
    }
    
    return true;
  });

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Messages</h2>
        
        {/* Search */}
        <div className="relative mb-4">
          <input
            type="text"
            placeholder={
              activeTab === 'support'
                ? 'Search support tickets...'
                : activeTab === 'direct'
                  ? 'Search direct messages...'
                  : 'Search Licences...'
            }
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full h-10 pl-10 pr-4 bg-[#F8F9FA] border border-[#EBEBEB] rounded-lg text-[14px] text-[#212529] placeholder-[#73827C] focus:outline-none focus:ring-2 focus:ring-[#40C191] focus:border-transparent"
          />
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            stroke="#73827C"
            strokeWidth="1.5"
          >
            <path
              d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M14 14L11 11"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        
        {/* Organization Type Filter — licence threads only */}
        {activeTab !== 'direct' && activeTab !== 'support' && (
        <div className="mb-4">
          <select
            value={organizationType}
            onChange={(e) => onOrganizationTypeChange(e.target.value)}
            className="w-full h-10 px-3 bg-[#F8F9FA] border border-[#EBEBEB] rounded-lg text-[14px] text-[#212529] focus:outline-none focus:ring-2 focus:ring-[#40C191] focus:border-transparent"
          >
            <option value="">All organisation Types</option>
            <option>Academia – Non-commercial</option>
            <option>Academia – Commercially funded </option>
            <option>Publicly funded healthcare provider </option>
            <option>Privately funded healthcare provider </option>
            <option>Publisher – Educational use</option>
            <option>Individual Clinical Practice – Not for profit</option>
            <option>Individual Clinical Practice – For profit</option>
            <option>Nutraceutical Company</option>
            <option>Contract Research Organisation (CRO)</option>
            <option>Commercial Research – Pharma or medical device company</option>
            {/* <option>IT Company – Access only (No sublicensing)</option>
            <option>IT Company – Full rights</option> */}
            <option>Translator / Translation Company</option>
        
          </select>
        </div>
        )}
        
        {/* Tabs */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={() => onTabChange('applicants')}
            className={`px-2 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'applicants'
                ? 'bg-[#EBF9F3] text-[#40C191]'
                : 'bg-[#F8F9FA] text-[#73827C] hover:bg-[#EBF9F3]'
            }`}
          >
            Applicants
          </button>
          <button
            onClick={() => onTabChange('direct')}
            className={`relative px-2 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'direct'
                ? 'bg-[#EBF9F3] text-[#40C191]'
                : 'bg-[#F8F9FA] text-[#73827C] hover:bg-[#EBF9F3]'
            }`}
          >
            <span className="inline-flex items-center justify-center gap-1.5">
              Direct
              {directUnread > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                  {directUnread > 99 ? '99+' : directUnread}
                </span>
              )}
            </span>
          </button>
          <button
            onClick={() => onTabChange('staff')}
            className={`px-2 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'staff'
                ? 'bg-[#EBF9F3] text-[#40C191]'
                : 'bg-[#F8F9FA] text-[#73827C] hover:bg-[#EBF9F3]'
            }`}
          >
            Staff
          </button>
          <button
            onClick={() => onTabChange('support')}
            className={`relative px-2 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'support'
                ? 'bg-[#EBF9F3] text-[#40C191]'
                : 'bg-[#F8F9FA] text-[#73827C] hover:bg-[#EBF9F3]'
            }`}
          >
            <span className="inline-flex items-center justify-center gap-1.5">
              Support
              {supportUnread > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                  {supportUnread > 99 ? '99+' : supportUnread}
                </span>
              )}
            </span>
          </button>
        </div>
        
        {/* Filters */}
        <div className="flex gap-2">
          <button
            onClick={() => onFilterChange('all')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-[#EBF9F3] text-[#40C191]'
                : 'bg-[#F8F9FA] text-[#73827C] hover:bg-[#EBF9F3]'
            }`}
          >
            All
          </button>
          <button
            onClick={() => onFilterChange('unread')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === 'unread'
                ? 'bg-[#EBF9F3] text-[#40C191]'
                : 'bg-[#F8F9FA] text-[#73827C] hover:bg-[#EBF9F3]'
            }`}
          >
            Unread {stats.unread > 0 && ` ${stats.unread}`}
          </button>
          <button
            onClick={() => onFilterChange('unanswered')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              filter === 'unanswered'
                ? 'bg-[#EBF9F3] text-[#40C191]'
                : 'bg-[#F8F9FA] text-[#73827C] hover:bg-[#EBF9F3]'
            }`}
          >
            Unanswered {stats.unanswered > 0 && ` ${stats.unanswered}`}
          </button>
        </div>
      </div>
      
      {/* Message List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {filteredConversations.length === 0 ? (
          <div className="text-center text-gray-500 text-sm py-8">
            {searchQuery ? 'No conversations found' : 'No messages yet'}
          </div>
        ) : (
          filteredConversations.map((conversation) => (
            <MessageListItem
              key={
                conversation.isSupportTicket
                  ? `ticket-${conversation.ticketId}`
                  : conversation.isDirectMessage
                    ? `direct-${conversation.directCustomerId}`
                    : `licence-${conversation.licenceId}`
              }
              conversation={conversation}
              isActive={
                conversation.isSupportTicket
                  ? selectedConversation?.ticketId === conversation.ticketId
                  : conversation.isDirectMessage
                    ? selectedConversation?.directCustomerId === conversation.directCustomerId
                    : selectedConversation?.licenceId === conversation.licenceId
              }
              onClick={onSelectConversation}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default MessageList;

